import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-01.svg";
import { PostPurchasePageStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { AutomationForgeElementType } from "@redotech/redo-model/forge/marketing-automation-forge-types";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { Handle, Position } from "@xyflow/react";
import { memo } from "react";
import { FlowEdge, NodeTypes, TypedFlowNode } from "../flow-converter";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as css from "./node.module.css";

export interface PostPurchasePageNodeData {
  step: PartialWithRequiredType<PostPurchasePageStep>;
  deleteElements: (id: string) => void;
  readonly: boolean;
}

export const PostPurchasePageNode = memo(function PostPurchasePageNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: PostPurchasePageNodeData;
  selected?: boolean | undefined;
}) {
  return (
    <BaseNode
      containerClassName={css.checkoutNode}
      icon={<RedoFeaturedIcon color="gray" Icon={ShoppingCartIcon} size="sm" />}
      onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
      selected={selected}
      step={data.step}
      title="Post-purchase page"
    >
      <Handle id="top" position={Position.Top} type="target" />
      <Flex align="flex-start" dir="column" gap="xs">
        <PageDetails step={data.step} />
      </Flex>
      <Handle id="bottom" position={Position.Bottom} type="source" />
    </BaseNode>
  );
});

const PageDetails = memo(function PageDetails({
  step,
}: {
  step: PartialWithRequiredType<PostPurchasePageStep>;
}) {
  return (
    <>
      <Text fontSize="xxs" textColor="secondary">
        Name
      </Text>
      <Flex w="full">
        <RedoBadge color="gray" size="xs" text={step.name ?? "Untitled"} />
      </Flex>
    </>
  );
});

export function getPostPurchasePageEdgesAndDropZones({
  stepId,
  acceptNextId,
  declineNextId,
  elementDropped,
  readonly,
}: {
  stepId: string;
  acceptNextId: string | undefined;
  declineNextId: string | undefined;
  elementDropped: (
    elementType: AutomationForgeElementType,
    idFrom: string,
    idTo?: string,
    conditionSide?: boolean,
  ) => void;
  readonly: boolean;
}): { edges: FlowEdge[]; dropZoneNodes: TypedFlowNode[] } {
  const edges: FlowEdge[] = [];
  const dropZoneNodes: TypedFlowNode[] = [];

  // Handle accept path
  if (isDef(acceptNextId)) {
    edges.push({
      id: stepId + "_accept",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: acceptNextId,
      data: {
        label: "Accept",
        color: "gray",
        idFrom: stepId,
        idTo: acceptNextId,
        conditionSide: true,
        ...(!readonly ? { elementDropped } : {}),
      },
    });
  } else if (!readonly) {
    const dropZoneId = stepId + "_accept";
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: {
        idFrom: stepId,
        idTo: undefined,
        conditionSide: true,
        elementDropped,
      },
    });
    edges.push({
      id: stepId + "_accept_edge",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: { label: "Accept", color: "gray" },
    });
  }

  // Handle decline path
  if (isDef(declineNextId)) {
    edges.push({
      id: stepId + "_decline",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: declineNextId,
      data: {
        label: "Decline",
        color: "gray",
        idFrom: stepId,
        idTo: declineNextId,
        conditionSide: false,
        ...(!readonly ? { elementDropped } : {}),
      },
    });
  } else if (!readonly) {
    const dropZoneId = stepId + "_decline";
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: {
        idFrom: stepId,
        idTo: undefined,
        conditionSide: false,
        elementDropped,
      },
    });
    edges.push({
      id: stepId + "_decline_edge",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: { label: "Decline", color: "gray" },
    });
  }

  return { edges, dropZoneNodes };
}
